<template>
  <div v-allowed="'manage_academy_groups'" class="pa-4">
    <!-- No group found -->
    <div v-if="!loading && !group" class="text-center pa-8">
      <v-icon size="64" color="grey lighten-2">mdi-domain</v-icon>
      <div class="text-h6 grey--text mt-4">{{ $t('message.academy_groups') }}</div>
      <div class="text-body-2 grey--text">{{ $t('message.no_group_configured') }}</div>
    </div>

    <div v-else>
      <v-row align="center" class="mb-2">
        <v-col class="d-flex align-center" style="gap:12px;">
          <img v-if="group && group.groupLogo" :src="group.groupLogo"
            style="width:48px;height:48px;object-fit:contain;border-radius:50%;background:#f5f5f5;" />
          <div>
            <h2 class="text-h5 font-weight-bold mb-0">{{ group ? group.name : '' }}</h2>
            <div v-if="group && group.address" class="text-caption grey--text">{{ group.address }}</div>
          </div>
          <v-btn icon small class="ml-1" @click="openGroupEdit">
            <v-icon small>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <image-upload-croppie
            v-if="group && ($store.state.permissions.includes('root') || isAppAdmin($store.state.user && $store.state.user._id))"
            :success-function="onLogoUploadSuccess" :vm="this" label="Logo"></image-upload-croppie>
        </v-col>
      </v-row>

      <v-tabs v-model="tab" class="mb-4">
        <v-tab>{{ $t('message.group_academies') }}</v-tab>
        <v-tab>{{ $t('message.group_dashboard') }}</v-tab>
      </v-tabs>

      <v-skeleton-loader v-if="loading" type="table"></v-skeleton-loader>

      <v-tabs-items v-else v-model="tab">

        <!-- ── Tab 1: Academies ────────────────────────────────────────── -->
        <v-tab-item>
          <v-card outlined class="mb-3">
            <v-card-actions>
              <v-btn-toggle v-model="viewMode" mandatory dense>
                <v-btn value="list" small>
                  <v-icon small>mdi-format-list-bulleted</v-icon>
                </v-btn>
                <v-btn value="map" small class="ml-1">
                  <v-icon small>mdi-map-outline</v-icon>
                </v-btn>
              </v-btn-toggle>
              <v-spacer></v-spacer>
              <span v-if="selectedAcademies.length > 0" class="text-caption grey--text mr-3">
                {{ selectedAcademies.length }} {{ $t('message.academies') }} &middot; {{ selectedMemberCount }} {{ $t('message.members') }}
              </span>
              <v-btn outlined small color="primary" :disabled="selectedAcademies.length === 0"
                @click="sendEmailDialog = true">
                <v-icon small left>mdi-email-outline</v-icon>{{ $t('message.send_email') }}
              </v-btn>
            </v-card-actions>
            <div v-if="viewMode === 'map'" class="d-flex justify-center pb-3">
              <v-btn-toggle v-model="mapDataMode" mandatory rounded color="primary" dense>
                <v-btn value="data" small class="px-6 font-roboto text-capitalize">
                  <v-icon small class="mr-0 mr-sm-2">mdi-image-outline</v-icon>
                  <span class="d-none d-sm-inline">Data</span>
                </v-btn>
                <v-btn value="mei" small class="px-6 font-roboto text-capitalize">
                  <v-icon small class="mr-0 mr-sm-2">mdi-gauge</v-icon>
                  <span class="d-none d-sm-inline">MEI</span>
                </v-btn>
              </v-btn-toggle>
            </div>
          </v-card>

          <!-- Map view -->
          <div v-if="viewMode === 'map'" style="height:520px;border-radius:8px;overflow:hidden;">
            <GmapMap :center="mapCenter" :zoom="mapZoom" map-type-id="roadmap" :options="{ disableDefaultUI: false }"
              style="width:100%;height:100%;" ref="map">
              <GmapInfoWindow :options="{ pixelOffset: { width: 0, height: -35 } }" :position="infoWindowPos"
                :opened="infoWinOpen" @closeclick="infoWinOpen = false">
                <div v-if="selectedPin" class="pa-2" style="min-width:200px;">
                  <div v-if="group && group.groupLogo" class="mb-2 text-center">
                    <img :src="group.groupLogo"
                      style="max-width:80px;max-height:36px;object-fit:contain;border-radius:6px;" />
                  </div>
                  <div class="font-weight-bold">{{ selectedPin.name }}</div>
                  <div class="text-caption">{{ selectedPin.address }}</div>
                  <div class="text-caption mt-1">
                    <country-flag-display :countryCode="selectedPin.country" :size="18" tile />
                    <span class="ml-1">{{ selectedPin.country }}</span>
                  </div>
                  <v-chip x-small class="mt-1" :color="selectedPin.isManaged ? 'success' : 'grey'" dark>
                    {{ selectedPin.isManaged ? $t('message.is_managed') : $t('message.not_managed') }}
                  </v-chip>
                  <div v-if="selectedPin.totalMembers !== null" class="text-caption mt-1">
                    {{ $t('message.members') }}: <b>{{ selectedPin.totalMembers }}</b>
                  </div>
                  <div v-if="selectedPin.meiAvg !== null" class="text-caption">
                    {{ $t('message.avg_mei') }}: <b>{{ selectedPin.meiAvg }}</b>
                  </div>
                </div>
              </GmapInfoWindow>
              <GmapMarker v-for="(m, i) in mapMarkers" :key="i" :position="m.position" :clickable="true" :icon="m.icon"
                @click="onMarkerClick(m)" />
            </GmapMap>
          </div>

          <!-- List view -->
          <v-data-table v-else :headers="academyHeaders" :items="academies" :loading="loadingAcademies" show-select
            v-model="selectedAcademies" :item-key="'_id'" class="elevation-0 outlined-table">
            <template v-slot:[`item.name`]="{ item }">
              <a :href="'https://connect.bjjlink.com/secure/academy/' + item._id" target="_blank" rel="noopener"
                class="font-weight-medium">{{ item.name }}</a>
            </template>
            <template v-slot:[`item.country`]="{ item }">
              <span v-if="item.country" class="d-inline-flex align-center" style="gap:6px;">
                <country-flag-display :countryCode="item.country" :size="20" tile />
                {{ item.country }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:[`item.isManaged`]="{ item }">
              <v-chip x-small :color="item.isManaged ? 'success' : 'grey lighten-2'" :dark="item.isManaged">
                {{ item.isManaged ? $t('message.is_managed') : $t('message.not_managed') }}
              </v-chip>
            </template>
            <template v-slot:[`item.meiAvg`]="{ item }">
              <span v-if="item.meiAvg !== null" :style="{ color: meiColor(item.meiAvg) }" class="font-weight-medium">{{ item.meiAvg }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:[`item.totalCheckins`]="{ item }">
              <span v-if="item.totalCheckins !== null && item.totalCheckins !== undefined">{{ item.totalCheckins }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:[`item.totalMembers`]="{ item }">
              <span v-if="item.totalMembers !== null">{{ item.totalMembers }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn v-if="!item.isManaged" icon x-small @click="openEditDialog(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- ── Tab 2: Dashboard ───────────────────────────────────────── -->
        <v-tab-item>
          <v-row v-if="dashboard">
            <!-- KPI row: Members + Avg Group MEI -->
            <v-col cols="12" sm="6">
              <v-card outlined class="pa-4 text-center">
                <div class="text-h4 font-weight-bold primary--text">{{ dashboard.totalMembers }}</div>
                <div class="text-caption">{{ $t('message.total_members') }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6">
              <v-card outlined class="pa-4 text-center">
                <div v-if="dashboard.avgGroupMei !== null && dashboard.avgGroupMei !== undefined"
                  class="text-h4 font-weight-bold" :style="{ color: meiColor(dashboard.avgGroupMei) }">
                  {{ dashboard.avgGroupMei }}
                </div>
                <div v-else class="text-h4 grey--text">—</div>
                <div class="text-caption">{{ $t('message.avg_mei') }} ({{ $t('message.group') }})</div>
              </v-card>
            </v-col>

            <!-- Belt distribution -->
            <v-col cols="12">
              <v-card outlined class="pa-4">
                <div class="text-subtitle-2 mb-2 font-weight-bold">{{ $t('message.members_by_belt') }}</div>
                <apexchart v-if="beltChartOptions" type="donut" height="260" :options="beltChartOptions"
                  :series="beltSeries" />
              </v-card>
            </v-col>

            <!-- MEI chart with region/top10 toggle -->
            <v-col cols="12">
              <v-card outlined class="pa-4">
                <div class="d-flex align-center mb-3">
                  <span class="text-subtitle-2 font-weight-bold">{{ $t('message.mei_by_region') }}</span>
                  <v-spacer></v-spacer>
                  <v-btn-toggle v-model="meiView" mandatory rounded color="primary" dense>
                    <v-btn value="region" x-small class="px-3 font-roboto text-capitalize">
                      {{ $t('message.region') }}
                    </v-btn>
                    <v-btn value="top10" x-small class="px-3 font-roboto text-capitalize">Top 10</v-btn>
                  </v-btn-toggle>
                </div>
                <apexchart v-if="meiChartOptions" type="bar" height="280" :options="meiChartOptions"
                  :series="meiSeries" />
              </v-card>
            </v-col>

            <!-- Checkins chart with region/top10 toggle -->
            <v-col cols="12">
              <v-card outlined class="pa-4">
                <div class="d-flex align-center mb-3">
                  <span class="text-subtitle-2 font-weight-bold">{{ $t('message.checkins_by_region') }}</span>
                  <v-spacer></v-spacer>
                  <v-btn-toggle v-model="checkinView" mandatory rounded color="primary" dense>
                    <v-btn value="region" x-small class="px-3 font-roboto text-capitalize">
                      {{ $t('message.region') }}
                    </v-btn>
                    <v-btn value="top10" x-small class="px-3 font-roboto text-capitalize">Top 10</v-btn>
                  </v-btn-toggle>
                </div>
                <apexchart v-if="checkinChartOptions" type="bar" height="320" :options="checkinChartOptions"
                  :series="checkinSeries" />
              </v-card>
            </v-col>
          </v-row>
          <v-skeleton-loader v-else type="article, article"></v-skeleton-loader>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- ── Edit group info dialog ───────────────────────────────────── -->
    <v-dialog v-model="groupEditDialog" max-width="440">
      <v-card v-if="editingGroup" outlined>
        <v-card-title>{{ $t('message.edit') }}: {{ group ? group.name : '' }}</v-card-title>
        <v-card-text>
          <v-text-field outlined dense :label="$t('message.name')" v-model="editingGroup.name"></v-text-field>
          <v-text-field outlined dense :label="$t('message.address')" v-model="editingGroup.address"
            :hint="$t('message.group_address_hint')" persistent-hint></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn small elevation="0" @click="groupEditDialog = false">{{ $t('message.cancel') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn small outlined elevation="0" color="primary" :loading="savingGroup" @click="saveGroup">
            {{ $t('message.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Edit academy dialog (unmanaged only) ──────────────────────── -->
    <v-dialog v-model="editDialog" max-width="500">
      <v-card v-if="editingAcademy" outlined>
        <v-card-title>{{ $t('message.edit') }}: {{ editingAcademy.name }}</v-card-title>
        <v-card-text>
          <v-text-field outlined dense :label="$t('message.name')" v-model="editingAcademy.name"></v-text-field>
          <v-text-field outlined dense :label="$t('message.address')" v-model="editingAcademy.address"></v-text-field>
          <v-text-field outlined dense :label="$t('message.city')" v-model="editingAcademy.cityDisplay"></v-text-field>
          <v-text-field outlined dense :label="$t('message.country')" v-model="editingAcademy.country"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn small elevation="0" @click="editDialog = false">{{ $t('message.cancel') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn small outlined elevation="0" color="primary" :loading="savingEdit" @click="saveAcademy()">
            {{ $t('message.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Send email dialog ─────────────────────────────────────────── -->
    <v-dialog v-model="sendEmailDialog" max-width="700">
      <v-card v-if="sendEmailDialog" outlined>
        <v-card-title>
          {{ $t('message.send_email_to') }}: <b class="ml-1">{{ selectedMemberCount }} {{ $t('message.members') }}</b>
        </v-card-title>
        <v-card-text>
          <v-select outlined dense clearable item-text="name" item-value="_id" v-model="selectedEmailTemplate"
            @change="onTemplateSelected" :label="$t('message.select_email_template')"
            :items="emailTemplates"></v-select>
          <v-text-field outlined dense :label="$t('message.subject')" v-model="emailSubject"></v-text-field>
          <!-- Template HTML preview -->
          <v-card v-if="emailHtmlPreview && !startNewEmail" outlined class="mb-3">
            <v-card-text>
              <div style="max-height:220px;overflow-y:auto;background:#fff;color:#333;" v-html="emailHtmlPreview"></div>
            </v-card-text>
          </v-card>
          <!-- Option to compose new / override -->
          <v-switch v-if="!startNewEmail" v-model="startNewEmail"
            :label="$t('message.start_new_email')" dense hide-details class="mt-0 mb-3"></v-switch>
          <template v-if="startNewEmail">
            <v-switch v-model="startNewEmail" :label="$t('message.start_new_email')"
              dense hide-details class="mt-0 mb-3"></v-switch>
            <vue-editor style="background-color:white;color:black;" v-model="emailEditor"
              :editor-toolbar="getEditorToolbar()" />
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn small elevation="0" @click="sendEmailDialog = false">{{ $t('message.cancel') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn small outlined elevation="0" color="primary" :loading="sendingEmail"
            :disabled="!emailSubject || (!selectedEmailTemplate && !emailEditor)" @click="sendEmail()">
            {{ $t('message.send') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CountryFlagDisplay from "@/components/displays/CountryFlagDisplay";
import ImageUploadCroppie from "@/components/inputs/ImageUploadCroppie";
import Api from "@/services/Api";
import VueApexCharts from "vue-apexcharts";
import { VueEditor } from "vue2-editor";

export default {
  name: "ManageAcademyGroups",
  components: { apexchart: VueApexCharts, ImageUploadCroppie, CountryFlagDisplay, VueEditor },
  data() {
    return {
      loading: true,
      loadingAcademies: false,
      tab: 0,
      group: null,
      academies: [],
      viewMode: "list",
      selectedAcademies: [],

      // Map
      mapCenter: { lat: 20, lng: -30 },
      mapZoom: 2,
      mapMarkers: [],
      mapDataMode: 'data',
      infoWinOpen: false,
      infoWindowPos: null,
      selectedPin: null,

      // Dashboard
      dashboard: null,
      meiView: 'region',
      checkinView: 'region',
      beltChartOptions: null,
      beltSeries: [],
      meiChartOptions: null,
      meiSeries: [],
      checkinChartOptions: null,
      checkinSeries: [],

      // Edit dialog
      editDialog: false,
      editingAcademy: null,
      savingEdit: false,
      groupEditDialog: false,
      editingGroup: null,
      savingGroup: false,

      // Email dialog
      sendEmailDialog: false,
      emailTemplates: [],
      selectedEmailTemplate: null,
      emailSubject: "",
      emailHtmlPreview: null,
      emailEditor: null,
      startNewEmail: false,
      sendingEmail: false,

      academyHeaders: [
        { text: this.$t("message.name"), value: "name" },
        { text: this.$t("message.address"), value: "address" },
        { text: this.$t("message.country"), value: "country" },
        { text: this.$t("message.is_managed"), value: "isManaged" },
        { text: this.$t("message.members"), value: "totalMembers" },
        { text: this.$t("message.avg_mei"), value: "meiAvg" },
        { text: this.$t("message.total_checkins"), value: "totalCheckins" },
        { text: this.$t("message.admins"), value: "totalAdmins" },
        { text: "", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    selectedMemberCount() {
      return this.selectedAcademies
        .filter(a => a.isManaged)
        .reduce((sum, a) => sum + (a.totalMembers || 0), 0);
    },
  },
  async created() {
    await this.loadGroup();
    this.loadEmailTemplates();
  },
  watch: {
    tab(val) {
      if (val === 1 && !this.dashboard) this.loadDashboard();
    },
    async viewMode(val) {
      if (val === 'map') await this.applyMapIcons();
    },
    async mapDataMode() {
      if (this.viewMode === 'map') await this.applyMapIcons();
    },
    meiView() {
      if (this.dashboard) this.buildMeiChart(this.dashboard);
    },
    checkinView() {
      if (this.dashboard) this.buildCheckinChart(this.dashboard);
    },
  },
  methods: {
    openGroupEdit() {
      this.editingGroup = { name: this.group ? this.group.name : '', address: this.group ? this.group.address || '' : '' };
      this.groupEditDialog = true;
    },
    async saveGroup() {
      if (!this.editingGroup || !this.group) return;
      this.savingGroup = true;
      try {
        await Api().patch(
          `groups/managedacademy/${this.$store.state.managedAcademy}/groups/${this.group._id}`,
          { name: this.editingGroup.name, address: this.editingGroup.address },
        );
        this.groupEditDialog = false;
        await this.loadGroup();
      } catch (e) {
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
      } finally {
        this.savingGroup = false;
      }
    },
    onLogoUploadSuccess(data, vm) {
      Api()
        .patch(
          `groups/managedacademy/${vm.$store.state.managedAcademy}/groups/${vm.group._id}`,
          { groupLogo: data.location },
        )
        .then(() => vm.loadGroup())
        .catch(() => { });
    },
    async loadGroup() {
      this.loading = true;
      try {
        const res = await Api().get(
          `groups/managedacademy/${this.$store.state.managedAcademy}/groups`,
        );
        if (res.data.groups && res.data.groups.length) {
          this.group = res.data.groups[0];
          await this.loadAcademies();
        }
      } catch (e) {
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
      } finally {
        this.loading = false;
      }
    },
    async loadAcademies() {
      this.loadingAcademies = true;
      try {
        const res = await Api().get(
          `groups/managedacademy/${this.$store.state.managedAcademy}/groups/${this.group._id}/academies`,
        );
        this.academies = res.data.academies || [];
        this.buildMapMarkers();
      } finally {
        this.loadingAcademies = false;
      }
    },
    createCircularIcon(url, size) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          ctx.beginPath();
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, size, size);
          const scale = Math.min(size / img.width, size / img.height);
          const w = img.width * scale;
          const h = img.height * scale;
          ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(url);
        img.src = url;
      });
    },
    buildMapMarkers() {
      this.mapMarkers = this.academies
        .filter(a => a.latitude && a.longitude)
        .map(a => ({
          position: { lat: a.latitude, lng: a.longitude },
          academy: a,
          icon: undefined,
        }));
      if (this.mapMarkers.length) {
        this.mapCenter = this.mapMarkers[0].position;
      }
    },
    async applyMapIcons() {
      try {
        await this.$gmapApiPromiseLazy();
        const g = window.google;
        if (!g || !g.maps) return;

        if (this.mapDataMode === 'mei') {
          this.mapMarkers = this.mapMarkers.map(m => ({
            ...m,
            icon: {
              url: this.createMeiIcon(m.academy.meiAvg, 26),
              scaledSize: new g.maps.Size(26, 26),
              anchor: new g.maps.Point(13, 13),
            },
          }));
        } else {
          if (!this.group || !this.group.groupLogo) return;
          const circularUrl = await this.createCircularIcon(this.group.groupLogo, 26);
          const iconConfig = {
            url: circularUrl,
            scaledSize: new g.maps.Size(26, 26),
            anchor: new g.maps.Point(13, 13),
          };
          this.mapMarkers = this.mapMarkers.map(m => ({ ...m, icon: iconConfig }));
        }
      } catch (e) {
        // Leave default markers on any failure
      }
    },
    meiColor(score) {
      if (score === null || score === undefined) return '#9E9E9E';
      if (score >= 80) return this.themeColor('success');
      if (score <= 20) return this.themeColor('error');
      const t = (score - 20) / 60;
      const r = Math.round(244 + (76 - 244) * t);
      const g = Math.round(67 + (175 - 67) * t);
      const b = Math.round(54 + (80 - 54) * t);
      return 'rgb(' + r + ',' + g + ',' + b + ')';
    },
    createMeiIcon(score, size) {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      const color = this.meiColor(score);
      const radius = size / 2;
      ctx.beginPath();
      ctx.arc(radius, radius, radius - 1, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 2;
      ctx.stroke();
      return canvas.toDataURL('image/png');
    },
    onMarkerClick(marker) {
      this.selectedPin = marker.academy;
      this.infoWindowPos = marker.position;
      this.infoWinOpen = true;
    },
    async loadDashboard() {
      try {
        const res = await Api().get(
          `groups/managedacademy/${this.$store.state.managedAcademy}/groups/${this.group._id}/dashboard`,
        );
        this.dashboard = res.data;
        this.buildCharts(res.data);
      } catch (e) {
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
      }
    },
    buildCharts(data) {
      this.buildBeltChart(data);
      this.buildMeiChart(data);
      this.buildCheckinChart(data);
    },
    buildBeltChart(data) {
      if (!data.belts || !data.belts.length) return;
      const beltCounts = {};
      for (const b of data.belts) {
        const base = b._id ? b._id.split('_')[0].replace(/\d+$/, '') : 'unknown';
        beltCounts[base] = (beltCounts[base] || 0) + b.count;
      }
      const ORDER = ['white', 'grey', 'yellow', 'orange', 'green', 'blue', 'purple', 'brown', 'black', 'red'];
      const sorted = Object.keys(beltCounts).sort((a, b) => {
        const ai = ORDER.indexOf(a), bi = ORDER.indexOf(b);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      const BELT_COLOR_OVERRIDES = { white: '#D4D4D4', grey: '#9E9E9E', unknown: '#BDBDBD' };
      this.beltSeries = sorted.map(b => beltCounts[b]);
      this.beltChartOptions = {
        labels: sorted,
        colors: sorted.map(b => BELT_COLOR_OVERRIDES[b] || this.getColor(b) || '#9E9E9E'),
        legend: { position: 'bottom' },
        stroke: { width: 2 },
        chart: { background: 'transparent' },
        theme: { mode: this.$vuetify.theme.dark ? 'dark' : 'light' },
        tooltip: { theme: 'dark' },
      };
    },
    buildMeiChart(data) {
      if (!data.mei || !data.mei.length) return;
      const dark = this.$vuetify.theme.dark;
      if (this.meiView === 'region') {
        const byRegion = {}, countByRegion = {};
        for (const m of data.mei) {
          const region = (data.academyMap && data.academyMap[m._id] && data.academyMap[m._id].region) || 'Other';
          byRegion[region] = (byRegion[region] || 0) + m.avgMei;
          countByRegion[region] = (countByRegion[region] || 0) + 1;
        }
        const REGION_ORDER = ['NorthAmerica', 'LatinAmerica', 'Europe', 'Oceania', 'Other'];
        const regions = REGION_ORDER.filter(r => byRegion[r]);
        const values = regions.map(r => Math.round(byRegion[r] / countByRegion[r]));
        this.meiSeries = [{ name: this.$t('message.avg_mei'), data: values }];
        this.meiChartOptions = {
          xaxis: { categories: regions },
          colors: values.map(s => this.meiColor(s)),
          chart: { toolbar: { show: false }, background: 'transparent' },
          theme: { mode: dark ? 'dark' : 'light' },
          plotOptions: { bar: { borderRadius: 4, distributed: true } },
          legend: { show: false },
        };
      } else {
        const top10 = [...data.mei].sort((a, b) => b.avgMei - a.avgMei).slice(0, 10);
        const labels = top10.map(m => (data.academyMap && data.academyMap[m._id] ? data.academyMap[m._id].name : m._id));
        const values = top10.map(m => Math.round(m.avgMei));
        this.meiSeries = [{ name: this.$t('message.avg_mei'), data: values }];
        this.meiChartOptions = {
          xaxis: { categories: labels, labels: { rotate: -35, trim: true } },
          colors: values.map(s => this.meiColor(s)),
          chart: { toolbar: { show: false }, background: 'transparent' },
          theme: { mode: dark ? 'dark' : 'light' },
          plotOptions: { bar: { borderRadius: 4, distributed: true } },
          legend: { show: false },
        };
      }
    },
    buildCheckinChart(data) {
      if (!data.checkins || !data.checkins.length) return;
      const dark = this.$vuetify.theme.dark;
      const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthSet = new Set();
      for (const c of data.checkins) {
        monthSet.add(`${c._id.year}-${String(c._id.month).padStart(2, '0')}`);
      }
      const months = Array.from(monthSet).sort();
      const monthLabels = months.map(m => {
        const [y, mo] = m.split('-');
        return `${MONTH_NAMES[parseInt(mo) - 1]} ${y}`;
      });

      if (this.checkinView === 'region') {
        const byRegionMonth = {};
        for (const c of data.checkins) {
          const region = (data.academyMap && data.academyMap[c._id.managedAcademy] && data.academyMap[c._id.managedAcademy].region) || 'Other';
          const key = `${c._id.year}-${String(c._id.month).padStart(2, '0')}`;
          if (!byRegionMonth[region]) byRegionMonth[region] = {};
          byRegionMonth[region][key] = (byRegionMonth[region][key] || 0) + c.count;
        }
        const REGION_ORDER = ['NorthAmerica', 'LatinAmerica', 'Europe', 'Oceania', 'Other'];
        const regions = REGION_ORDER.filter(r => byRegionMonth[r]);
        this.checkinSeries = regions.map(r => ({
          name: r,
          data: months.map(m => byRegionMonth[r][m] || 0),
        }));
      } else {
        const totalByAcademy = {};
        for (const c of data.checkins) {
          const key = c._id.managedAcademy;
          totalByAcademy[key] = (totalByAcademy[key] || 0) + c.count;
        }
        const top10keys = Object.entries(totalByAcademy)
          .sort((a, b) => b[1] - a[1]).slice(0, 10).map(([k]) => k);
        const byAcademyMonth = {};
        for (const c of data.checkins) {
          if (!top10keys.includes(c._id.managedAcademy)) continue;
          const key = c._id.managedAcademy;
          const m = `${c._id.year}-${String(c._id.month).padStart(2, '0')}`;
          if (!byAcademyMonth[key]) byAcademyMonth[key] = {};
          byAcademyMonth[key][m] = c.count;
        }
        this.checkinSeries = top10keys.map(key => ({
          name: data.academyMap && data.academyMap[key] ? data.academyMap[key].name : key,
          data: months.map(m => (byAcademyMonth[key] && byAcademyMonth[key][m]) || 0),
        }));
      }
      this.checkinChartOptions = {
        xaxis: { categories: monthLabels },
        chart: { stacked: true, toolbar: { show: false }, background: 'transparent' },
        theme: { mode: dark ? 'dark' : 'light' },
        plotOptions: { bar: { borderRadius: 2 } },
        tooltip: { shared: false, intersect: true },
      };
    },
    openEditDialog(academy) {
      this.editingAcademy = { ...academy };
      this.editDialog = true;
    },
    async saveAcademy() {
      this.savingEdit = true;
      try {
        await Api().patch(
          `groups/managedacademy/${this.$store.state.managedAcademy}/groups/${this.group._id}/academies/${this.editingAcademy._id}`,
          {
            name: this.editingAcademy.name,
            address: this.editingAcademy.address,
            cityDisplay: this.editingAcademy.cityDisplay,
            country: this.editingAcademy.country,
          },
        );
        this.showAlert("success", this.$t("message.updated"));
        this.editDialog = false;
        await this.loadAcademies();
      } catch (e) {
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
      } finally {
        this.savingEdit = false;
      }
    },
    loadEmailTemplates() {
      Api()
        .get(`documents/academy/${this.$store.state.managedAcademy}/email/templates?archived=false`)
        .then(res => {
          if (res.data && res.data.templates) this.emailTemplates = res.data.templates;
        });
    },
    onTemplateSelected(templateId) {
      this.startNewEmail = false;
      this.emailEditor = null;
      if (!templateId) { this.emailHtmlPreview = null; return; }
      const t = this.emailTemplates.find(t => t._id === templateId);
      if (t) {
        this.emailHtmlPreview = t.contents || null;
        if (t.subject) this.emailSubject = t.subject;
      }
    },
    async sendEmail() {
      if (!this.emailSubject) return;
      const contents = this.startNewEmail ? this.emailEditor : null;
      if (!this.selectedEmailTemplate && !contents) return;
      const managedIds = this.selectedAcademies
        .filter(a => a.managedAcademyId)
        .map(a => a.managedAcademyId);
      if (!managedIds.length) return;
      this.sendingEmail = true;
      try {
        await Api().post(
          `groups/managedacademy/${this.$store.state.managedAcademy}/groups/${this.group._id}/email`,
          {
            managedAcademyIds: managedIds,
            emailTemplate: this.selectedEmailTemplate || undefined,
            contents: contents || undefined,
            subject: this.emailSubject,
          },
        );
        this.showAlert("success", this.$t("message.success"));
        this.sendEmailDialog = false;
        this.selectedAcademies = [];
        this.emailSubject = "";
        this.selectedEmailTemplate = null;
        this.emailHtmlPreview = null;
        this.emailEditor = null;
        this.startNewEmail = false;
      } catch (e) {
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
      } finally {
        this.sendingEmail = false;
      }
    },
  },
};
</script>
